/**
 * Background Task Scheduler
 * 
 * Handles background task coordination, lifecycle management, and scheduled
 * sync execution with retry logic and error handling.
 */

import { SyncStrategyScheduler } from './SyncStrategyScheduler';
import { SyncScheduleConfig, ScheduledSync } from './BaseScheduler';

/**
 * Background Task Scheduler
 * 
 * Extends SyncStrategyScheduler to add background task execution capabilities,
 * including sync execution, retry logic, and task lifecycle management.
 */
export class BackgroundTaskScheduler extends SyncStrategyScheduler {
  constructor(config: Partial<SyncScheduleConfig> = {}) {
    super(config);
  }

  /**
   * Execute pending syncs with error handling and retry logic
   */
  protected async executePendingSyncs(): Promise<void> {
    console.log('📅 Executing scheduled sync...');

    try {
      // Check if sync should be postponed
      const postponeReason = this.shouldPostponeSync();
      if (postponeReason) {
        this.postponeSync(postponeReason);
        return;
      }

      // Execute the sync
      const { syncService } = await import('@/services/offline');
      await syncService.performSync();

      // Schedule next sync
      this.scheduleNextSync('normal', 'completed_sync');
    } catch (error) {
      console.error('❌ Scheduled sync failed:', error);
      
      // Schedule retry with exponential backoff
      const retryInterval = Math.min(this.currentInterval * 2, this.config.maxInterval);
      setTimeout(() => {
        this.scheduleNextSync('normal', 'retry_after_failure');
      }, retryInterval);
    }
  }

  /**
   * Check if sync should be postponed
   */
  protected shouldPostponeSync(): string | null {
    // Check battery level
    if (this.config.respectBatteryLevel && 
        this.batteryLevel <= this.config.criticalBatteryThreshold) {
      return `Critical battery level: ${this.batteryLevel}%`;
    }

    // Check quiet hours
    if (this.config.enableQuietHours && this.isQuietHours()) {
      return 'Quiet hours active';
    }

    // Check if we're in a low activity period and should reduce frequency
    if (this.config.adaptToUsagePatterns) {
      const currentHour = new Date().getHours();
      const hourlyActivity = this.usagePattern.hourlyActivity[currentHour] || 0;
      
      if (hourlyActivity < 0.1) {
        return 'Very low activity period';
      }
    }

    return null;
  }

  /**
   * Check if current time is within quiet hours
   */
  protected isQuietHours(): boolean {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    const start = this.config.quietHoursStart;
    const end = this.config.quietHoursEnd;

    // Handle overnight quiet hours (e.g., 22:00 to 07:00)
    if (start > end) {
      return currentTime >= start || currentTime <= end;
    }

    // Handle same-day quiet hours (e.g., 13:00 to 14:00)
    return currentTime >= start && currentTime <= end;
  }

  /**
   * Postpone sync to a later time
   */
  private postponeSync(reason: string): void {
    const originalTime = this.nextSyncTime || new Date().toISOString();
    
    // Calculate postpone interval based on reason
    let postponeInterval: number;
    
    if (reason.includes('battery')) {
      postponeInterval = this.config.batteryOptimizedInterval;
    } else if (reason.includes('quiet')) {
      postponeInterval = this.config.quietHoursInterval;
    } else {
      postponeInterval = this.config.defaultInterval;
    }

    const newTime = new Date(Date.now() + postponeInterval).toISOString();

    this.emitEvent({
      type: 'sync_postponed',
      data: {
        reason,
        newTime,
        originalTime
      }
    });

    console.log(`📅 Sync postponed: ${reason}. Rescheduled for ${new Date(newTime).toLocaleString()}`);
    
    // Reschedule with the postpone interval
    setTimeout(() => {
      this.scheduleNextSync('normal', 'postponed_sync_retry');
    }, postponeInterval);
  }

  /**
   * Execute a specific scheduled sync task
   */
  async executeScheduledSync(syncId: string): Promise<boolean> {
    const sync = this.scheduledSyncs.get(syncId);
    if (!sync) {
      console.warn(`Scheduled sync ${syncId} not found`);
      return false;
    }

    try {
      console.log(`📅 Executing scheduled sync: ${sync.reason} (${sync.priority} priority)`);

      // Check if we should postpone this specific sync
      const postponeReason = this.shouldPostponeSync();
      if (postponeReason) {
        this.postponeSpecificSync(sync, postponeReason);
        return false;
      }

      // Execute the sync
      const { syncService } = await import('@/services/offline');
      await syncService.performSync();

      // Remove completed sync
      this.scheduledSyncs.delete(syncId);
      
      console.log(`✅ Scheduled sync completed: ${sync.reason}`);
      return true;
    } catch (error) {
      console.error(`❌ Scheduled sync failed: ${sync.reason}`, error);
      
      // Increment attempt count
      sync.attempts += 1;
      
      if (sync.attempts >= sync.maxAttempts) {
        console.error(`❌ Scheduled sync exceeded max attempts: ${sync.reason}`);
        this.scheduledSyncs.delete(syncId);
        return false;
      }

      // Reschedule with exponential backoff
      const backoffDelay = Math.min(
        this.config.defaultInterval * Math.pow(2, sync.attempts),
        this.config.maxInterval
      );
      
      sync.scheduledTime = new Date(Date.now() + backoffDelay).toISOString();
      
      console.log(`🔄 Retrying scheduled sync in ${backoffDelay}ms (attempt ${sync.attempts}/${sync.maxAttempts})`);
      return false;
    }
  }

  /**
   * Postpone a specific scheduled sync
   */
  private postponeSpecificSync(sync: ScheduledSync, reason: string): void {
    const originalTime = sync.scheduledTime;
    
    // Calculate postpone interval
    let postponeInterval: number;
    
    if (reason.includes('battery')) {
      postponeInterval = this.config.batteryOptimizedInterval;
    } else if (reason.includes('quiet')) {
      postponeInterval = this.config.quietHoursInterval;
    } else {
      postponeInterval = this.config.defaultInterval;
    }

    sync.scheduledTime = new Date(Date.now() + postponeInterval).toISOString();

    this.emitEvent({
      type: 'sync_postponed',
      data: {
        reason: `${sync.reason}: ${reason}`,
        newTime: sync.scheduledTime,
        originalTime
      }
    });

    console.log(`📅 Scheduled sync postponed: ${sync.reason} - ${reason}`);
  }

  /**
   * Process all pending scheduled syncs
   */
  async processPendingSyncs(): Promise<void> {
    const now = Date.now();
    const pendingSyncs = Array.from(this.scheduledSyncs.entries())
      .filter(([_, sync]) => new Date(sync.scheduledTime).getTime() <= now)
      .sort(([_, a], [__, b]) => {
        // Sort by priority (high first) then by scheduled time
        const priorityOrder = { high: 0, normal: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        
        return new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime();
      });

    if (pendingSyncs.length === 0) {
      return;
    }

    console.log(`📅 Processing ${pendingSyncs.length} pending syncs...`);

    for (const [syncId] of pendingSyncs) {
      try {
        await this.executeScheduledSync(syncId);

        // Add small delay between syncs to avoid overwhelming the system
        if (pendingSyncs.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`Error processing sync ${syncId}:`, error);
      }
    }
  }

  /**
   * Cancel a scheduled sync
   */
  cancelScheduledSync(syncId: string): boolean {
    const sync = this.scheduledSyncs.get(syncId);
    if (!sync) {
      return false;
    }

    this.scheduledSyncs.delete(syncId);
    console.log(`📅 Cancelled scheduled sync: ${sync.reason}`);
    return true;
  }

  /**
   * Get pending syncs count
   */
  getPendingSyncsCount(): number {
    const now = Date.now();
    return Array.from(this.scheduledSyncs.values())
      .filter(sync => new Date(sync.scheduledTime).getTime() <= now)
      .length;
  }

  /**
   * Get upcoming syncs count
   */
  getUpcomingSyncsCount(): number {
    const now = Date.now();
    return Array.from(this.scheduledSyncs.values())
      .filter(sync => new Date(sync.scheduledTime).getTime() > now)
      .length;
  }
}
